import envVars from 'env/config';
import { busBandEditingStatus } from 'utils/busband';
import { DeviceListTypeId } from 'utils/enums/device';
import { SiteEventType } from 'utils/enums/siteEvent';
import { getCamelCase, getPointByName } from 'utils/propertyHelpers';
import { firstElement, getValueIfExists, propertyExist } from 'utils/propertyValidation';
import {
  CLEAN_SITE_AUDIT_LOG_FILTERS,
  DELETE_USER_PERMISSIONS,
  FETCH_BILLING_INFO,
  FETCH_BILLING_INFO_FAIL,
  FETCH_SITES_FAIL,
  FETCH_SITES_ICIAN_USERS_FAIL,
  FETCH_SITES_ICIAN_USERS_START,
  FETCH_SITES_START,
  FETCH_SITE_AUDIT_LOG,
  FETCH_SITE_AUDIT_LOG_FAIL,
  FETCH_SITE_BUS_BAND,
  FETCH_SITE_CONFIGURATION_FAIL,
  FETCH_SITE_DETAILS,
  FETCH_SITE_EVENT_COUNTS,
  FETCH_SITE_EVENT_COUNTS_FAIL,
  FETCH_SITE_EVENT_DAILY_COUNTS,
  FETCH_SITE_EVENT_DAILY_COUNTS_FAIL,
  FETCH_SITE_EVENTS,
  FETCH_SITE_OVERVIEW_DETAILS,
  FETCH_SITE_OVERVIEW_DETAILS_FAIL,
  FETCH_SITE_TELEMETRY,
  FETCH_SITE_TELEMETRY_FAIL,
  FETCH_SITE_TYPES_FAIL,
  FETCH_SITE_TYPES_START,
  FETCH_WORK_ORDERS_FAIL,
  PATCH_SITE_BUS_BAND_POINTS,
  PATCH_SITE_BUS_BAND_POINTS_FAIL,
  REDIRECTING_WITH_FILTERS,
  RESET_SITES_FILTERS,
  RESET_SITE_EVENTS,
  RESET_SITE_TYPE,
  SET_BILLING_INFO,
  SET_BUS_BAND_STATUS,
  SET_BUS_BAND_STATUS_CLEAN,
  SET_BUS_BAND_STATUS_FAIL,
  SET_BUS_BAND_STATUS_START,
  SET_CA_CERTIFICATE,
  SET_COMPANIES_SITES,
  SET_CRADLE_POINT_ROUTER_ID,
  SET_OKTA_USERS,
  SET_SITES,
  SET_SITES_FILTERS_AREA,
  SET_SITES_FILTERS_ASSIGNED_USER,
  SET_SITES_FILTERS_CUSTOMER,
  SET_SITES_FILTERS_EMULATED_SITES,
  SET_SITES_FILTERS_LOCATION,
  SET_SITES_FILTERS_STATUS,
  SET_SITES_FILTERS_TEXT,
  SET_SITES_ICIAN_USERS,
  SET_SITE_AUDIT_LOG,
  SET_SITE_AUDIT_LOG_FILTERS,
  SET_SITE_AUDIT_LOG_MORE_RESULTS,
  SET_SITE_BUS_BAND,
  SET_SITE_CONFIGURATION,
  SET_SITE_CONFIGURATION_FAIL,
  SET_SITE_DETAILS,
  SET_SITE_EVENT_COUNTS,
  SET_SITE_EVENT_DAILY_COUNTS,
  SET_SITE_EVENTS,
  SET_SITE_EVENT_FILTERS,
  SET_SITE_OVERVIEW_DETAILS,
  SET_SITE_TELEMETRY,
  SET_SITE_TYPE,
  SET_SITE_TYPES,
  SET_SITE_USERNAME_PASSWORD,
  SET_TARIFF_STRUCTURE,
  SET_TIME_ZONES,
  SET_USERS_PERMISSIONS,
  SET_WORK_ORDERS,
  SAVE_SITE_OVERVIEW_DETAILS,
  SAVE_SITE_OVERVIEW_DETAILS_FAIL,
  SAVE_SITE_OVERVIEW_DETAILS_SUCCESS,
  UPDATE_BUS_BAND_TABS_DISABLED,
  UPDATE_SITE_CONFIGURATION,
  UPDATE_TIME_ZONE,
  UPDATE_WORK_ORDERS_FAIL,
  UPDATE_WORK_ORDERS_START,
} from './types';

const initialState = {
  companiesSites: [],
  timeZones: [],
  siteTypes: [],
  oktaUsers: [],
  usersPermissions: {},
  allSites: [],
  icianUsers: [],
  loading: false,
  redirectingWithFilters: {},
  filters: {
    text: '',
    status: [],
    customer: [],
    area: [],
    location: [],
    assignedUser: '',
    showEmulatedSites: false,
    isAreaDropdownDisabled: true,
    isLocationDropwonDisabled: true,
  },
  site: {
    siteId: '',
    name: '',
    location: {
      customer: {
        primaryContact: {},
      },
      address: {},
    },
    address: {},
    timeZone: { country: '', name: '', timeZoneId: 0 },
    siteConfigurationData: {},
    workOrderData: {},
    type: '',
    siteType: {
      siteTypeId: 0,
      name: '',
    },
    plcId: '',
    password: '',
    caCertificate: {
      url: '',
    },
    tariffStructure: {},
    busBand: {
      configuration: {},
      formattedConfiguration: {},
      lastPointsUpdated: {},
      editionAvailable: {
        status: null,
        message: '',
      },
    },
    busbandStateId: null,
    userEditingBusband: null,
    events: [],
    eventCounts: [],
    eventDailyCounts: [],
    overview: null,
    overviewDetails: null,
    overviewDetailsLoading: false,
    overviewDetailsSaving: false,
    overviewDetailsError: null,
    eventsFilters: {
      eventTypeId: SiteEventType.AllEvents,
      deviceId: 0,
      groupId: DeviceListTypeId.All,
    },
    auditLogs: {
      date: null,
      siteId: 0,
      page: 0,
      totalResults: 0,
      results: [],
    },
    auditLogsFilters: { text: '' },
    routerId: '',
    financials: {},
  },
  busBandTabsDisabled: false,
};

/**
 * formatBusBandData
 * Format BusBand object to create an array of all available values.
 * @param {Object} busBand BusBand Configuration Object to format
 * @returns {Object} returns a formatted object of the BusBand object
 */
export const formatBusBandData = (busBand) => {
  const defaultActiveColumnIndex = 1;
  const activeColumn = firstElement(getValueIfExists(() => busBand.points, []));
  const formattedConfiguration = {
    busBandConfiguration: [],
    groups: [],
    rowsBasic: [],
    activeColumn: getValueIfExists(() => activeColumn.value, defaultActiveColumnIndex),
  };
  if (propertyExist(() => busBand.group) && busBand.group.length) {
    // Get columns names
    const busBandColumns = busBand.group.map((busBandItem) => ({
      formattedNameId: getCamelCase(busBandItem.name),
      nameId: busBandItem.name,
      name: busBandItem.label,
    }));
    formattedConfiguration.groups = busBandColumns;

    // Get rows names
    const elementsGroupBase = getValueIfExists(() => firstElement(busBand.group).points, []);
    const busBandRow = elementsGroupBase.map((point) => ({
      nameId: point.name,
      name: point.label,
    }));
    formattedConfiguration.rowsBasic = busBandRow;

    // Format to set the entry point into the value for each element on the array
    formattedConfiguration.busBandConfiguration = busBandRow.map((row) => {
      const columnItems = {};
      busBandColumns.forEach((column) => {
        const groupPoints = busBand.group.find((group) => group.name === column.nameId);
        const pointItem = getPointByName(
          getValueIfExists(() => groupPoints.points, []),
          row.nameId,
        );
        columnItems[`${column.formattedNameId}`] = pointItem;
      });
      return {
        id: row.nameId,
        name: row.name,
        ...columnItems,
      };
    });
  }
  return formattedConfiguration;
};

/**
 * formatSiteEvents it will format this array into another with the parent property at the same level.
 * @param {array} events it is an array of array to format
 * @returns {array} it returns a formatted flatted array
 */
const formatSiteEvents = (events) => {
  const formattedEventsArray = [];
  if (events) {
    // eslint-disable-next-line
    envVars.doLog && console.log('formatSiteEvents');
    events.forEach((siteEventGroup) => {
      if (!siteEventGroup || typeof siteEventGroup !== 'object') return;
      const { events: eventList = [] } = siteEventGroup;
      eventList.forEach((eventItem) => {
        if (!eventItem) return;
        formattedEventsArray.push({
          ...eventItem,
          eventTypeId: siteEventGroup.name,
          subType: eventItem.subType || siteEventGroup.name,
        });
      });
    });
  }
  return formattedEventsArray;
};

/**
 * @method formatTimeZonesData
 * Format time zones objects with new properties text and value, this for
 * using on dropdown data
 * @param {array} timeZones receives action.payload data from redux
 * @returns {array}
 */
const formatTimeZonesData = (timeZones) => {
  if (timeZones) {
    return timeZones.map((time) => ({
      ...time,
      value: time.timeZoneId,
      text: time.name,
    }));
  }
  return [];
};

/**
 * @method removeUserInUserPermissions
 * Remove user from user's array filtering by user ID
 * @param {array} users receives users array from redux
 * @param {number} userId receives user ID
 */
const removeUserInUserPermissions = (users, userId) =>
  users.filter((user) => user.userId !== userId);

/**
 * @method removeOktaUsersFromUserPermissions
 * Remove okta users which exist in user permissions
 * @param {Array} oktaUsers receives okta users array
 * @param {Array} usersPermissions receives user permissions array
 * @returns {Array}
 */
const removeOktaUsersFromUserPermissions = (oktaUsers, usersPermissions) =>
  oktaUsers.filter((user) => {
    if (!usersPermissions.filter((perm) => perm.externalId === user.profile.email).length) {
      return true;
    }
    return false;
  });

/**
 * sitesReducer
 * Selector method to get sites filtered based on stored filter object
 * @param {object} state current redux state for sites
 * @param {object} action action creator object
 */
const sitesReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case SET_SITES:
      return { ...state, allSites: action.payload };
    case FETCH_SITES_START:
      return { ...state, loading: true };
    case FETCH_SITES_FAIL:
      return { ...state, loading: false };

    case FETCH_SITE_TYPES_START:
      return { ...state, loading: true };
    case FETCH_SITE_TYPES_FAIL:
      return { ...state, loading: false };
    case SET_SITE_TYPES:
      return { ...state, loading: false, siteTypes: action.payload };

    case SET_SITES_ICIAN_USERS:
      return { ...state, icianUsers: action.payload };
    case FETCH_SITES_ICIAN_USERS_START:
      return { ...state, loading: true };
    case FETCH_SITES_ICIAN_USERS_FAIL:
      return { ...state, loading: false };

    case FETCH_SITE_AUDIT_LOG_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          auditLogs: [],
        },
      };

    case FETCH_SITE_DETAILS:
      return { ...state, loading: true };

    case FETCH_SITE_EVENTS:
      return { ...state, loading: true };

    case FETCH_SITE_TELEMETRY:
      return {
        ...state,
        loading: true,
        site: {
          ...state.site,
          overview: {},
        },
      };
    case FETCH_SITE_BUS_BAND:
      return { ...state, loading: true };
    case FETCH_SITE_AUDIT_LOG:
      return { ...state, loading: true };

    case SET_SITES_FILTERS_STATUS:
      return {
        ...state,
        filters: { ...state.filters, status: action.payload },
      };
    case SET_SITES_FILTERS_CUSTOMER:
      return {
        ...state,
        filters: {
          ...state.filters,
          customer: action.payload,
          isAreaDropdownDisabled: false,
        },
      };
    case SET_SITES_FILTERS_AREA:
      return {
        ...state,
        filters: {
          ...state.filters,
          area: action.payload,
          isLocationDropwonDisabled: false,
        },
      };
    case SET_SITES_FILTERS_LOCATION:
      return {
        ...state,
        filters: { ...state.filters, location: action.payload },
      };
    case RESET_SITE_EVENTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          events: [],
        },
      };
    case RESET_SITES_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          isLocationDropwonDisabled: true,
          isAreaDropdownDisabled: true,
          status: [],
          customer: [],
          area: [],
          location: [],
        },
      };
    case REDIRECTING_WITH_FILTERS:
      return {
        ...state,
        redirectingWithFilters: action.payload,
      };
    case SET_SITES_FILTERS_TEXT:
      return {
        ...state,
        filters: { ...state.filters, text: action.payload },
      };
    case SET_SITES_FILTERS_ASSIGNED_USER:
      return {
        ...state,
        filters: { ...state.filters, assignedUser: action.payload },
      };
    case SET_SITES_FILTERS_EMULATED_SITES:
      return {
        ...state,
        filters: { ...state.filters, showEmulatedSites: action.payload },
      };
    case SET_SITE_DETAILS:
      return {
        ...state,
        loading: false,
        site: {
          ...action.payload,
          events: state.site.events,
          eventsFilters: state.site.eventsFilters,
          overview: state.site.overview,
          overviewDetails: state.site.overviewDetails,
          overviewDetailsLoading: state.site.overviewDetailsLoading,
          overviewDetailsSaving: state.site.overviewDetailsSaving,
          overviewDetailsError: state.site.overviewDetailsError,
          auditLogs: state.site.auditLogs,
          auditLogsFilters: state.site.auditLogsFilters,
          busBand: state.site.busBand,
        },
      };
    case SET_SITE_EVENTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          events: formatSiteEvents(action.payload),
        },
      };

    case FETCH_SITE_EVENT_COUNTS:
      return { ...state, loading: true };

    case SET_SITE_EVENT_COUNTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          eventCounts: action.payload,
        },
      };

    case FETCH_SITE_EVENT_COUNTS_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          eventCounts: [],
        },
      };

    case FETCH_SITE_EVENT_DAILY_COUNTS:
      return { ...state, loading: true };

    case SET_SITE_EVENT_DAILY_COUNTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          eventDailyCounts: action.payload,
        },
      };

    case FETCH_SITE_EVENT_DAILY_COUNTS_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          eventDailyCounts: [],
        },
      };
    case SET_BILLING_INFO:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          financials: action.payload[0],
        },
      };
    case FETCH_BILLING_INFO_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          financials: {},
        },
      };
    case FETCH_BILLING_INFO:
      return { ...state, loading: true };
    case SET_SITE_EVENT_FILTERS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          eventsFilters: action.payload,
        },
      };
    case SET_SITE_TELEMETRY:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          overview: action.payload,
        },
      };
    case FETCH_SITE_OVERVIEW_DETAILS:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetailsLoading: true,
          overviewDetailsError: null,
        },
      };
    case SET_SITE_OVERVIEW_DETAILS:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetails: action.payload,
          overviewDetailsLoading: false,
          overviewDetailsSaving: false,
          overviewDetailsError: null,
        },
      };
    case FETCH_SITE_OVERVIEW_DETAILS_FAIL:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetails: null,
          overviewDetailsLoading: false,
          overviewDetailsError: action.payload,
        },
      };
    case SAVE_SITE_OVERVIEW_DETAILS:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetailsSaving: true,
          overviewDetailsError: null,
        },
      };
    case SAVE_SITE_OVERVIEW_DETAILS_SUCCESS:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetailsSaving: false,
          overviewDetailsError: null,
        },
      };
    case SAVE_SITE_OVERVIEW_DETAILS_FAIL:
      return {
        ...state,
        site: {
          ...state.site,
          overviewDetailsSaving: false,
          overviewDetailsError: action.payload,
        },
      };
    case FETCH_SITE_TELEMETRY_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          overview: {},
        },
      };
    case SET_SITE_BUS_BAND:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            configuration: action.payload,
            formattedConfiguration: formatBusBandData(action.payload),
            lastPointsUpdated: {},
          },
        },
      };

    case PATCH_SITE_BUS_BAND_POINTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            lastPointsUpdated: action.payload,
          },
        },
      };

    case PATCH_SITE_BUS_BAND_POINTS_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            lastPointsUpdated: { error: action.payload },
          },
        },
      };

    case SET_BUS_BAND_STATUS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            editionAvailable: {
              status: busBandEditingStatus.Available,
              message: action.payload.userEditingBusband,
            },
          },
        },
      };
    case SET_BUS_BAND_STATUS_START:
      return {
        ...state,
        loading: true,
      };

    case SET_BUS_BAND_STATUS_FAIL:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            editionAvailable: {
              status: busBandEditingStatus.Unavailable,
              message: action.payload,
            },
          },
        },
      };

    case SET_BUS_BAND_STATUS_CLEAN:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          busBand: {
            ...state.site.busBand,
            editionAvailable: {},
          },
        },
      };

    case SET_SITE_AUDIT_LOG:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          auditLogs: action.payload,
        },
      };
    case SET_SITE_AUDIT_LOG_FILTERS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          auditLogFilters: action.payload,
        },
      };
    case SET_SITE_AUDIT_LOG_MORE_RESULTS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          auditLogs: {
            ...action.payload,
            results: [...state.site.auditLogs.results, ...action.payload.results],
          },
        },
      };
    case SET_CA_CERTIFICATE:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          caCertificate: action.payload,
        },
      };
    case CLEAN_SITE_AUDIT_LOG_FILTERS:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          auditLogFilters: { text: '' },
        },
      };
    case SET_OKTA_USERS:
      return {
        ...state,
        oktaUsers: removeOktaUsersFromUserPermissions(action.payload, state.usersPermissions.users),
      };
    case SET_USERS_PERMISSIONS:
      return {
        ...state,
        usersPermissions: action.payload,
      };
    case UPDATE_TIME_ZONE:
      return {
        ...state,
        site: {
          ...state.site,
          timeZone: action.payload,
        },
      };
    case SET_TIME_ZONES:
      return {
        ...state,
        timeZones: formatTimeZonesData(action.payload),
      };
    case SET_SITE_CONFIGURATION_FAIL:
      return {
        ...state,
        loading: false,
        siteConfiguration: {},
      };
    case UPDATE_SITE_CONFIGURATION:
      return {
        ...state,
        siteConfiguration: action.payload,
      };
    case FETCH_SITE_CONFIGURATION_FAIL:
      return {
        ...state,
        loading: false,
        siteConfiguration: {},
      };
    case SET_SITE_CONFIGURATION:
      return {
        ...state,
        siteConfigurationData: action.payload,
      };
    case DELETE_USER_PERMISSIONS:
      return {
        ...state,
        usersPermissions: {
          ...state.usersPermissions,
          users: removeUserInUserPermissions(state.usersPermissions.users, action.payload),
        },
      };
    case UPDATE_WORK_ORDERS_START:
      return {
        ...state,
        loading: true,
        workOrderData: {},
      };
    case UPDATE_WORK_ORDERS_FAIL:
      return {
        ...state,
        loading: false,
        workOrderData: {},
      };
    case SET_WORK_ORDERS:
      return {
        ...state,
        loading: false,
        workOrderData: action.payload,
      };
    case FETCH_WORK_ORDERS_FAIL:
      return {
        ...state,
        loading: false,
        workOrderData: {},
      };
    case RESET_SITE_TYPE:
      return {
        ...state,
        site: initialState.site,
      };
    case SET_SITE_TYPE:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          siteType: {
            siteTypeId: action.payload.selectedSiteTypeId,
            name: action.payload.selectedSiteTypeName,
          },
        },
      };
    case SET_SITE_USERNAME_PASSWORD:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          plcId: action.payload.plcId,
          password: action.payload.password,
        },
      };
    case SET_TARIFF_STRUCTURE:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          tariffStructure: action.payload,
        },
      };
    case SET_COMPANIES_SITES:
      return {
        ...state,
        companiesSites: action.payload,
      };
    case SET_CRADLE_POINT_ROUTER_ID:
      return {
        ...state,
        loading: false,
        site: {
          ...state.site,
          routerId: action.payload,
        },
      };
    case UPDATE_BUS_BAND_TABS_DISABLED:
      return {
        ...state,
        busBandTabsDisabled: action.payload,
      };
    default:
      return state;
  }
};

export default sitesReducer;
